import React, { useState } from 'react'

import { SxProps } from '@mui/material'

import { TGroup } from 'types/GlobalGroup'

import { useAccount, useAccounts } from 'redux/hooks/Accounts'
import { useGenericSource } from 'redux/hooks/Resources'
import { useGroups } from 'redux/hooks/Groups'
import { useKeyMetric } from 'redux/hooks/KeyMetrics'
import { useTask } from 'redux/hooks/Tasks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Divider from 'components_new/atoms/Divider'
import Text from 'components_new/atoms/Text'

import EditableTitle from 'components_new/molecules/EditableTitle'
import GenericAvatar from 'components_new/molecules/GenericAvatar'
import GroupsChip from 'components_new/molecules/GroupsChip'
import { OptionProps } from 'components_new/molecules/OptionsMenu'
import IconButton from 'components_new/atoms/IconButton'

import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import RichTextEditor from 'components_new/molecules/RichTextEditor'
import { toEditorValue } from 'components_new/molecules/RichTextEditor/contract'
import SearchAndSelectPopover from 'components_new/molecules/SearchAndSelectPopover'
import UserAvatar from 'components_new/molecules/UserAvatar'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import GroupsDialog from 'components_new/organisms/dialogs/GroupsDialog'
import MetricPreview from 'components_new/organisms/MetricPreview'

import { Highlighted } from 'components_new/organisms/StandardSearch/utils'

import { getParsedDate } from 'utils/dateParser'
import { formatUsername } from 'utils/functions'
import { useTaskActions } from 'redux/actions/Tasks'

export interface TaskPreviewProps {
  disableBackground?: boolean
  editMode?: boolean
  hideLinkedKeyMetric?: boolean
  hideStatus?: boolean
  id: string
  onClick?: () => void
  onEdit?: () => void
  search?: string
  sx?: SxProps
}

const TaskPreview = (props: TaskPreviewProps) => {
  const {
    disableBackground,
    editMode,
    hideLinkedKeyMetric,
    hideStatus,
    id,
    onClick,
    onEdit,
    search,
    sx
  } = props

  const { t, translateEnum } = useTranslation()
  const { accounts } = useAccounts()

  const { allGroups } = useGroups()
  const task = useTask(id)
  const keyMetric = useKeyMetric(task?.key_metric_id ?? null)
  const genericSource = useGenericSource(
    task?.source_type ?? null,
    task?.source_id ?? null
  )
  const responsibleAccount = useAccount(task?.responsible_account_id ?? null)
  const { tryDeleteTask, tryUpdateTask } = useTaskActions()

  // dialogs
  const [dialog, setDialog] = useState<{
    open: boolean
    variant: 'delete' | 'groups'
  }>({
    open: false,
    variant: 'groups'
  })

  const handleClickGroups = () => {
    setDialog((prevState) => ({
      ...prevState,
      open: true,
      variant: 'groups'
    }))
  }

  const handleCloseDialog = () => {
    setDialog((prevState) => ({
      ...prevState,
      id: '',
      open: false
    }))
  }

  if (!task) return null // $TODO: loading

  const handleAddGroup = (groupId: string) => {
    tryUpdateTask(id, {
      group_ids: [...task.group_ids, groupId]
    })
  }

  const handleRemoveGroup = (groupId: string) => {
    tryUpdateTask(id, {
      group_ids: task.group_ids.filter((gid) => gid !== groupId)
    })
  }

  const options: OptionProps[] = [
    ...(onEdit
      ? [
          {
            iconName: 'Edit' as const,
            onClick: onEdit,
            title: t('global', 'edit')
          }
        ]
      : []),
    {
      color: 'error' as const,
      iconName: 'Delete' as const,
      onClick: () => setDialog({ open: true, variant: 'delete' }),
      title: t('global', 'delete')
    }
  ]

  return (
    <>
      <Box
        onClick={onClick}
        sx={{
          ...(disableBackground
            ? undefined
            : {
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }),
          cursor: onClick ? 'pointer' : undefined,
          px: 2,
          py: 1,
          '&:hover': {
            borderColor: disableBackground ? undefined : 'primary.main',
            ['& .OptionsMenuButton']: {
              color: 'primary.main'
            },
            ['& .EditTitleButton']: {
              opacity: 1
            }
          },
          ...sx
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 0.5,
              mb: 0.5
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {search ? (
                <Text noWrap variant="subtitle1">
                  <Highlighted
                    highlight={search}
                    text={task.title || t('global', 'unnamed')}
                  />
                </Text>
              ) : (
                <EditableTitle
                  actions={true}
                  editIcon={true}
                  disabled={!editMode}
                  onSubmit={(value) =>
                    tryUpdateTask(id, { title: value ?? '' })
                  }
                  value={task.title}
                  variant="subtitle1"
                />
              )}
            </Box>

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.25 }}>
              {hideStatus ? null : (
                <Chip
                  color={translateEnum('task_status', task.status).color}
                  label={translateEnum('task_status', task.status).title}
                  size="xsmall"
                />
              )}

              {editMode && options && options.length > 0 ? (
                <Box
                  className="OptionsMenuButton"
                  sx={{
                    color: 'action.disabled'
                  }}
                >
                  <OptionsMenuButton
                    edge="end"
                    options={options}
                    size="small"
                    variant="icon"
                  />
                </Box>
              ) : null}
            </Box>
          </Box>

          {task.description ? (
            <RichTextEditor
              color="text.secondary"
              edit={false}
              maxLines={2}
              noGutter={true}
              paragraphVariant="body2"
              value={toEditorValue(task.description)}
              variant="outlined"
            />
          ) : null}

          {!hideLinkedKeyMetric && keyMetric ? (
            <MetricPreview
              border={true}
              id={keyMetric.metric.id}
              linked={true}
              keyMetric={keyMetric}
              settings={keyMetric.metric.settings}
              sx={{ mb: 1 }}
            />
          ) : !hideLinkedKeyMetric &&
            task.metric &&
            task.metric_filter_attribute_id &&
            task.metric_filter_value ? (
                <MetricPreview
                  border={true}
                  id={task.metric.id}
                  linked={true}
                  queryParams={{
                    filter: {
                      [task.metric_filter_attribute_id]: {
                        eq: [task.metric_filter_value]
                      }
                    }
                  }}
                  settings={task.metric.settings}
                  sx={{ mb: 1 }}
                />
              ) : null}

          <Divider sx={{ my: 1 }} />

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
              <Text color="text.disabled" component="div" variant="body2">
                {`${t('global', 'created')} ${getParsedDate(
                  task.created_at
                )} ${t('global', 'from', true)}`}{' '}
                <GenericAvatar
                  size="inherit"
                  sx={{ verticalAlign: 'middle' }}
                  title={genericSource.title}
                  variant={genericSource.variant}
                />{' '}
                <b>{genericSource.title}</b>
              </Text>
            </Box>

            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 0.5,
                justifyContent: 'space-between'
              }}
            >
              <GroupsChip
                groups={
                  task.group_ids.length > 0
                    ? task.group_ids
                      .filter((groupId) =>
                        allGroups.find((g) => g.id === groupId)
                      )
                      .map((groupId) => {
                        const group = allGroups.find(
                          (g) => g.id === groupId
                        ) as TGroup

                        return group
                      })
                    : []
                }
                onClick={handleClickGroups}
              />

              <Divider flexItem={true} orientation="vertical" />

              <SearchAndSelectPopover
                multiple={false}
                onChange={(value) =>
                  tryUpdateTask(id, { responsible_account_id: value })
                }
                options={accounts.map((account) => {
                  return {
                    avatar: 'user',
                    body: account.email,
                    id: account.id,
                    title: formatUsername(account)
                  }
                })}
                tooltip={
                  responsibleAccount
                    ? `${t('global', 'owner')}: ${formatUsername(
                      responsibleAccount
                    )}`
                    : t('global', 'no_owner')
                }
                value={task.responsible_account_id}
                sx={{ mr: -1 }}
              >
                <IconButton size="small">
                  <UserAvatar
                    email={
                      responsibleAccount ? responsibleAccount.email : undefined
                    }
                    name={
                      responsibleAccount
                        ? formatUsername(responsibleAccount)
                        : undefined
                    }
                    size="small"
                  />
                </IconButton>
              </SearchAndSelectPopover>
            </Box>
          </Box>
        </Box>
      </Box>

      {/*-- delete --*/}
      <DeleteDialog
        onClose={handleCloseDialog}
        onDelete={(cb) => {
          tryDeleteTask(id, (err) => {
            if (!err) handleCloseDialog()
            cb()
          })
        }}
        open={dialog.open && dialog.variant === 'delete'}
        title={task.title || t('global', 'unnamed')}
        type={t('tasks', 'the_task', true)}
      />

      <GroupsDialog
        groups={task.group_ids
          .filter((groupId) => allGroups.find((g) => g.id === groupId))
          .map((groupId) => allGroups.find((g) => g.id === groupId) as TGroup)}
        onAdd={handleAddGroup}
        onClose={handleCloseDialog}
        onRemove={handleRemoveGroup}
        open={dialog.open && dialog.variant === 'groups'}
      />
    </>
  )
}

export default TaskPreview
